import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCourse } from '../../context/CourseContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  BookOpen,
  Clock,
  Headphones,
  CheckCircle2,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react';

export const ChapterCard = ({ chapter, index }) => {
  const { getChapterProgress, isBookmarked, toggleBookmark } = useCourse();
  const progress = getChapterProgress(chapter.id);

  const getStatusBadge = () => {
    if (progress.completed) {
      return (
        <Badge className="bg-success text-success-foreground gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Completed
        </Badge>
      );
    }
    if (progress.started) {
      return (
        <Badge variant="outline" className="border-primary text-primary gap-1">
          <BookOpen className="w-3 h-3" />
          In Progress
        </Badge>
      );
    }
    return null;
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className={`chapter-card group bg-card border-silver/20 shadow-sm hover:shadow-silver ${
        progress.completed ? 'complete' : progress.started ? 'active' : ''
      }`}
    >
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={chapter.image}
          alt={chapter.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-maroon/10" />
        
        {/* Step Badge */}
        <div className="absolute top-4 left-4">
          <div className={`chapter-badge ${
            progress.completed ? 'complete' : progress.started ? 'active' : 'default'
          }`}>
            {progress.completed ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              chapter.id
            )}
          </div>
        </div>

        {/* Bookmark Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleBookmark(chapter.id);
          }}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center transition-all hover:bg-background hover:scale-110 hover:shadow-silver"
        >
          {isBookmarked(chapter.id) ? (
            <BookmarkCheck className="w-5 h-5 text-secondary" />
          ) : (
            <Bookmark className="w-5 h-5 text-muted-foreground" />
          )}
        </button>

        {/* Status Badge */}
        <div className="absolute bottom-4 left-4">
          {getStatusBadge()}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6 flex flex-col flex-1">
        {/* Step Label */}
        <span className="text-xs font-semibold uppercase tracking-wider text-maroon mb-2">
          {chapter.step}
        </span>

        {/* Title */}
        <h3 className="font-display text-xl font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
          {chapter.title}
        </h3>

        {/* Subtitle */}
        <p className="text-sm text-muted-foreground mb-4">
          {chapter.subtitle}
        </p>

        {/* Summary */}
        <p className="text-sm text-foreground/80 leading-relaxed mb-4 line-clamp-3 flex-1">
          {chapter.summary}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {chapter.duration}
          </span>
          <span className="flex items-center gap-1">
            <Headphones className="w-3.5 h-3.5" />
            {chapter.audioLength}
          </span>
        </div>

        {/* Progress Bar (if started) */}
        {progress.started && !progress.completed && (
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Reading Progress</span>
              <span className="text-primary font-medium">{progress.readingProgress || 0}%</span>
            </div>
            <Progress value={progress.readingProgress || 0} className="h-1.5" />
          </div>
        )}

        {/* CTA Button */}
        <Link to={`/chapter/${chapter.id}`} className="mt-auto">
          <Button 
            className={`w-full gap-2 ${
              progress.completed 
                ? 'bg-success hover:bg-success/90 text-success-foreground' 
                : 'btn-teal'
            }`}
          >
            {progress.completed ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Review Chapter
              </>
            ) : progress.started ? (
              <>
                Continue Reading
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Start Chapter
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </Link>
      </div>
    </motion.article>
  );
};

export default ChapterCard;
